import React, { useEffect, useRef, useState } from 'react';
import { buttonClass, IconButton } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import SearchField from '../components/ui/SearchField';
import Tag from '../components/ui/Tag';
import { IconClose } from '../components/ui/icons';
import Dialog from '../components/ui/Dialog';
import EmptyState from '../components/ui/EmptyState';
import Reveal from '../components/ui/Reveal';
import FadeIn from '../components/ui/FadeIn';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import { socColor } from './chargeUi';
import useChargeStore from '../stores/useChargeStore';
import { useToast } from '../components/Toast/ToastContext';
import type { ChargerView } from './types';

interface Props {
  onClose: () => void;
  onOpenCharger: (c: ChargerView) => void;
}

/** Find a specific charger across the WHOLE fleet (internal + customer). The
 *  only way a customer-owned charger surfaces — always badged. Pin to add it to
 *  My Chargers, or tap to open its detail. */
export default function ChargerSearch({ onClose, onOpenCharger }: Props) {
  const { searchChargers, pinCharger, unpinCharger, findSameNetwork } = useChargeStore();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ChargerView[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  // After pinning a charger, offer its online neighbors on the same network.
  const [sameNet, setSameNet] = useState<{ triggerLabel: string; list: ChargerView[] } | null>(null);
  const seq = useRef(0);

  // Debounced; queries at 2+ chars so this is "find a specific charger", not a
  // fleet dump. A stale response can't overwrite a newer one (seq guard).
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setSearched(false); setLoading(false); return; }
    const id = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchChargers(term);
        if (seq.current === id) { setResults(r); setSearched(true); }
      } catch {
        if (seq.current === id) setResults([]);
      } finally {
        if (seq.current === id) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, searchChargers]);

  const togglePin = async (c: ChargerView) => {
    if (!c.asset_id) return;
    try {
      if (c.pinned) {
        await unpinCharger(c.asset_id); toast.success('Removed from My Chargers');
        setResults((rs) => rs.map((r) => (r.asset_id === c.asset_id ? { ...r, pinned: false } : r)));
      } else {
        await pinCharger(c.asset_id); toast.success('Pinned to My Chargers');
        setResults((rs) => rs.map((r) => (r.asset_id === c.asset_id ? { ...r, pinned: true } : r)));
        // Suggest its ONLINE neighbors on the same network (same public IP).
        if (c.online && c.network_id) {
          const mates = (await findSameNetwork(c.network_id))
            .filter((m) => m.asset_id && m.asset_id !== c.asset_id && !m.pinned);
          setSameNet(mates.length ? { triggerLabel: c.display_code || c.vpn_ip, list: mates } : null);
        }
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update pin');
    }
  };

  const pinMate = async (m: ChargerView) => {
    if (!m.asset_id) return;
    try {
      await pinCharger(m.asset_id); toast.success('Pinned to My Chargers');
      setSameNet((s) => (s ? { ...s, list: s.list.filter((x) => x.asset_id !== m.asset_id) } : s));
      setResults((rs) => rs.map((r) => (r.asset_id === m.asset_id ? { ...r, pinned: true } : r)));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to pin');
    }
  };

  const term = q.trim();

  return (
    <Dialog open onClose={onClose} title="Find a charger" size="lg">
      <SearchField
        value={q} onChange={setQ} autoFocus
        placeholder="Search by code or IP…"
        label="Search chargers by code or IP"
        className="mb-3"
      />

      <Reveal show={!!sameNet && sameNet.list.length > 0}>
      {sameNet && sameNet.list.length > 0 && (
        <div className="mb-3 rounded border border-primary/40 bg-primary/5 p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-sm text-on-surface">
              Pin more on the same network as <span className="font-mono text-white">{sameNet.triggerLabel}</span>?
            </span>
            <Tooltip label="Dismiss"><IconButton size="sm" label="Dismiss" onClick={() => setSameNet(null)}><IconClose /></IconButton></Tooltip>
          </div>
          <div className="space-y-1">
            {sameNet.list.map((m) => (
              <div key={m.vpn_ip} className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full flex-shrink-0 bg-emerald-400" />
                <span className="text-white text-sm flex-shrink-0">{m.display_code || m.vpn_ip}</span>
                {m.geo_label && <span className="text-xs text-on-surface-variant truncate min-w-0">{m.geo_label}</span>}
                <button type="button" onClick={() => pinMate(m)}
                  className={`${buttonClass('quiet', 'sm')} ml-auto flex-shrink-0`}>Pin</button>
              </div>
            ))}
          </div>
        </div>
      )}
      </Reveal>

      <FadeIn key={term.length < 2 ? 'hint' : loading ? 'loading' : results.length === 0 && searched ? 'none' : 'results'}>
      {term.length < 2 ? (
        <EmptyState variant="area">Type at least 2 characters to search the fleet.</EmptyState>
      ) : loading ? (
        <SkeletonGroup label="Searching" className="space-y-1">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </SkeletonGroup>
      ) : results.length === 0 && searched ? (
        <EmptyState variant="area">No chargers match “{term}”.</EmptyState>
      ) : (
        <div className="space-y-1">
          {results.map((c) => (
            <div key={c.vpn_ip} className="flex items-center gap-2 px-2 py-2 rounded hover:bg-[#2a2a2a] transition">
              <button
                type="button" onClick={() => onOpenCharger(c)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
              >
                <span role="img" aria-label={c.online ? 'Online' : 'Offline'}
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${c.online ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                <span className="text-white text-sm flex-shrink-0">{c.display_code || c.vpn_ip}</span>
                {!c.is_internal_pool && (
                  <Tag tone="warn" size="sm" className="flex-shrink-0">Customer</Tag>
                )}
                {c.rollup?.soc_avg != null && (
                  <span className={`text-xs flex-shrink-0 ${socColor(c.rollup.soc_avg)}`}>{Math.round(c.rollup.soc_avg)}%</span>
                )}
                {c.geo_label && <span className="text-xs text-on-surface-variant truncate min-w-0">{c.geo_label}</span>}
              </button>
              <button
                type="button" onClick={() => togglePin(c)}
                aria-label={c.pinned ? 'Remove from My Chargers' : 'Pin to My Chargers'}
                className={`flex-shrink-0 text-xs px-2 py-1 rounded border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  c.pinned ? 'border-primary text-primary' : 'border-outline-variant text-on-surface-variant hover:text-on-surface'
                }`}
              >{c.pinned ? 'Pinned' : 'Pin'}</button>
            </div>
          ))}
        </div>
      )}
      </FadeIn>
    </Dialog>
  );
}
